import { spawnSync } from 'node:child_process'
import { existsSync } from 'node:fs'
import { chmod, mkdtemp, rm } from 'node:fs/promises'
import { tmpdir } from 'node:os'
import { join } from 'node:path'
import type { Context } from '@tnega/core'
import { localExecutionProvider } from '@tnega/execution'
import { containsDirectory } from '@tnega/fs-sandbox'
import { startNetworkProxy, type NetworkProxy, type ProxyDecision } from '@tnega/sandbox-network-proxy'
import {
  SandboxError,
  SandboxService,
  SandboxUnavailableError,
  canonicalPath,
  decideNetworkAccess,
  isNetworkRestricted,
  type ConfinedArgv,
  type ConfinedSandboxMode,
  type RunnerFailureRule,
  type SandboxBackendStatus,
  type SandboxEnforcement,
  type SandboxMechanismRequest,
  type SandboxNetworkPolicy,
  type SandboxPolicy,
} from '@tnega/sandbox'
import {
  LINUX_PROXY_PORT,
  bwrapProfileArgs,
  landlockProfileArgs,
  linuxRelayArgv,
  proxyEnvironment,
  seatbeltProfileArgs,
} from './profiles.js'

export { sandboxSetupRoutes, type SandboxNetworkSupport, type SandboxSetup } from './routes.js'

/** 本机可用的机制名。`custom` 是自带 runner 的逃生口（部署方提供实现）。 */
export type SandboxRunnerName = 'bwrap' | 'landlock' | 'seatbelt' | 'windows-mxc' | 'windows-acl' | 'custom'

/**
 * 平台 → 机制链。顺序就是优先级：链上第一个**功能性探测**通过的机制被选中。
 *
 * linux 选 bwrap 优先是因为它的 mount profile 与模式词汇最贴近（工作区可写 = 同路径
 * rw bind）；bwrap 不可用（例如 user namespace 被禁）时退到 landlock。
 *
 * win32 优先 MXC（Microsoft 维护、由策略驱动、不改写工作区的常驻 DACL）；宿主上没有 MXC
 * 执行器、系统低于 MXC 的版本下限，或执行器拒绝请求时，探测失败，退到原有的 ACL 受限令牌。
 */
const PLATFORM_CHAINS: Partial<Record<NodeJS.Platform, readonly SandboxRunnerName[]>> = {
  linux: ['bwrap', 'landlock'],
  darwin: ['seatbelt'],
  win32: ['windows-mxc', 'windows-acl'],
}

/**
 * 机制声明的强制完整度。只有 Windows ACL 是 `partial`：它授予的是**写**白名单
 * （`WRITE_RESTRICTED` 只交叉检查写访问），Everyone 仍在环境权限里，NTFS 硬链接还是
 * 文件对象别名。详见 `@tnega/sandbox-windows-acl` 的 README。
 */
const STATIC_ENFORCEMENT: Record<SandboxRunnerName, SandboxEnforcement> = {
  bwrap: 'full',
  landlock: 'full',
  seatbelt: 'full',
  // NTFS 硬链接别名、档位随宿主而变（base-container 档保留环境读权限）。
  'windows-mxc': 'partial',
  'windows-acl': 'partial',
  custom: 'full',
}

/** 各后端的**拒绝**方言。刻意不做并集：混在一起会把「runner 坏了」读成「被策略拒绝」。 */
const DENIAL_SIGNATURES: Record<SandboxRunnerName, readonly string[]> = {
  bwrap: ['read-only file system', 'Permission denied'],
  landlock: ['Permission denied'],
  seatbelt: ['Operation not permitted'],
  'windows-mxc': ['Access is denied', 'access is denied', '拒绝访问', 'Access to the path', 'EPERM'],
  'windows-acl': ['Access is denied', 'access is denied', 'EPERM'],
  custom: [],
}

/** 各后端 runner **自己失败**时的签名（区别于被策略拒绝）。 */
const RUNNER_FAILURE_RULES: Record<SandboxRunnerName, readonly RunnerFailureRule[]> = {
  bwrap: [{ fatalSignatures: ['bwrap: '] }],
  landlock: [{ allowedExitCodes: [125], fatalSignatures: ['landlock-run: '] }],
  seatbelt: [{ fatalSignatures: ['sandbox-exec: '] }],
  'windows-mxc': [{ allowedExitCodes: [127], fatalSignatures: ['mxc-sandbox-run: '] }],
  'windows-acl': [{ allowedExitCodes: [127], fatalSignatures: ['windows-acl-run: '] }],
  custom: [],
}

const DEFAULT_PROBE_TIMEOUT_MS = 5_000
/** MXC 的探测真的起一个容器，首次可能要几秒；未显式配置时给它更宽的预算。 */
const DEFAULT_MXC_PROBE_TIMEOUT_MS = 30_000

/** Windows ACL 机制包的形状：只有 win32 才会被动态加载。 */
type WindowsAclModule = typeof import('@tnega/sandbox-windows-acl')
/** MXC 机制包的形状：同样只在 win32 上加载。 */
type WindowsMxcModule = typeof import('@tnega/sandbox-windows-mxc')

interface AclGrant {
  add(path: string, options?: { revocable?: boolean }): Promise<void>
  revoke(path: string): Promise<void>
  dispose(): Promise<void>
}

type AclGrantOptions = Parameters<WindowsAclModule['AclWriteGrant']['create']>[1]

interface TempGrant {
  dir: string
  sid: string
  grant: AclGrant
}

/** 测试与部署方的注入点；生产路径不需要它。 */
export interface SandboxInternals {
  platform?: NodeJS.Platform
  chain?: readonly SandboxRunnerName[]
  /** 替换功能性探测；返回 `'unusable'` 表示该机制在本机不可用。 */
  probe?: (runner: SandboxRunnerName) => SandboxEnforcement | 'unusable'
  /** 替换「本机卷根」的枚举（`mxcRead: 'all'` 用）；只用于测试。 */
  volumeRoots?: () => readonly string[]
  /**
   * 替换网络能力的探测：返回 `undefined` 表示该机制能强制这种网络模式，返回字符串则是
   * 做不到的原因。只用于测试。
   */
  networkProbe?: (runner: SandboxRunnerName, mode: 'allowlist' | 'off') => string | undefined
}

export interface Config {
  /** 探测与无 Session 调用使用的根；默认 `process.cwd()`。 */
  workspaceRoot?: string
  /** 私有临时区的父目录；默认 `os.tmpdir()`。 */
  tempRoot?: string
  /** 探测单个机制的超时（毫秒），默认 5000。必须为正有限值。 */
  probeTimeoutMs?: number
  /** 覆盖机制链（顺序即优先级）；默认按平台。 */
  chain?: readonly SandboxRunnerName[]
  /** `custom` runner 的命令前缀。 */
  runnerCommand?: readonly string[]
  /** `custom` runner 的致命 stderr 签名（与 `runnerCommand` 同现同缺）。 */
  runnerFailureSignatures?: readonly string[]
  /** `custom` runner 的拒绝方言；默认空集，即只按失败规则判定。 */
  runnerDenialSignatures?: readonly string[]
  bwrapPath?: string
  landlockLauncher?: string
  sandboxExecPath?: string
  /** Windows ACL runner 的显式命令；默认由机制包自己解析。 */
  windowsAclRunnerCommand?: readonly string[]
  /** Windows MXC runner 的显式命令；默认由机制包自己解析。 */
  windowsMxcRunnerCommand?: readonly string[]
  /**
   * MXC 执行器 `wxc-exec.exe` 的路径。省略时依次看 `TNEGA_MXC_EXEC`、`@microsoft/mxc-sdk`
   * 载荷与 `PATH`；`TNEGA_MXC_EXEC=off` 关闭 MXC，直接用 windows-acl。
   */
  mxcExecPath?: string
  /** 固定 MXC 请求契约版本；省略时探测按 `MXC_CONTRACT_VERSIONS` 依次协商。 */
  mxcContractVersion?: string
  /**
   * MXC 的读策略：`all`（默认）把本机每个卷根（`C:\`、`D:\`…）作为只读根交给 MXC，
   * 因此**读不受限**，与 ACL 链的既有语义一致（它只收窄写）；`roots` 只放开工作区与
   * `mxcReadOnlyRoots`，用于需要读侧隔离的部署。
   */
  mxcRead?: 'all' | 'roots'
  /** `mxcRead: 'roots'` 时工作区之外命令可以**读**的目录（也总是叠加在 `all` 之上）。 */
  mxcReadOnlyRoots?: readonly string[]
  /** Linux 网络过滤用的 `socat`；默认按 `PATH` 找。 */
  socatPath?: string
  /**
   * 过滤代理放行之后经哪个上游代理出去（`http://host:port`），每次连接读一次。返回
   * `undefined` 时退回宿主的 `HTTPS_PROXY` / `HTTP_PROXY`；返回空串即直连。
   */
  upstreamProxy?: () => string | undefined
  /** 不走上游的目标（`NO_PROXY` 语法），默认读宿主的 `NO_PROXY`。 */
  noProxy?: () => string | undefined
  internals?: SandboxInternals
}

interface Selection {
  runner: SandboxRunnerName
  enforcement: SandboxEnforcement
}

/** 一份网络策略对应的过滤代理。 */
interface ProxyHandle {
  proxy: NetworkProxy
  /** Linux 上代理 socket 所在的私有目录；TCP 代理缺席。 */
  socketDir?: string
}

/** 宿主自己的上游代理：只认 http(s) 变量，`ALL_PROXY` 常常是 socks5://，过滤代理不会说。 */
function environmentUpstream(): string | undefined {
  for (const key of ['HTTPS_PROXY', 'https_proxy', 'HTTP_PROXY', 'http_proxy']) {
    const value = process.env[key]?.trim()
    if (value) return value
  }
  return undefined
}

/** 让模型读得懂的拦截说明，附在命令的 stderr 末尾。 */
function describeDenials(denials: readonly ProxyDecision[]): string | undefined {
  if (denials.length === 0) return undefined
  const seen = new Set<string>()
  const lines: string[] = []
  for (const denial of denials) {
    const target = `${denial.host.includes(':') ? `[${denial.host}]` : denial.host}:${denial.port}`
    if (seen.has(target)) continue
    seen.add(target)
    const why = denial.reason === 'denied'
      ? `matches the denied domain ${denial.rule ?? ''}`.trim()
      : denial.reason === 'local-address'
        ? 'resolves to a local address'
        : 'not in the allowed domains'
    lines.push(`  ${target} (${why})`)
    if (lines.length >= 10) break
  }
  return [
    'tnega sandbox blocked network access for this command:',
    ...lines,
    'Commands can only reach the allowed domains set in Settings › Tools & shell › Command network. '
    + 'If the task needs one of these, ask the user to allow it; do not try to work around the block.',
  ].join('\n')
}

/** 探测协商出的 MXC 执行器与契约版本。 */
interface MxcSelection {
  exec: string
  contract: string
}

let windowsAclModule: WindowsAclModule | undefined
let windowsMxcModule: WindowsMxcModule | undefined
/** 卷根枚举结果：盘符是否存在是进程生命周期内稳定的事实。 */
let volumeRootCache: readonly string[] | undefined

async function windowsMxc(): Promise<WindowsMxcModule> {
  windowsMxcModule ??= await import('@tnega/sandbox-windows-mxc')
  return windowsMxcModule
}

/** 只在真正需要 Windows 机制时加载 koffi —— POSIX 上不该付这份代价。 */
async function windowsAcl(): Promise<WindowsAclModule> {
  windowsAclModule ??= await import('@tnega/sandbox-windows-acl')
  return windowsAclModule
}

/**
 * 本机卷根（`C:\`、`D:\`…）。
 *
 * MXC 的 AppContainer 档位没有环境读权限：只授予工作区时，`node`、`npm`、`pwsh` 这些
 * 装在工作区之外的工具连加载都做不到（pwsh 7.7 之前甚至要求能读 `C:\` 根才肯启动）。
 * 只挑「工具根」是猜不准的 —— 每个用户的 node / 工具链盘符都不同，所以默认按卷放开读，
 * 与 ACL 链只写不读的语义对齐。
 *
 * 枚举用 `existsSync`，同步且不做子进程；结果缓存一次，且不存在的盘符查询是即时的。
 * 唯一代价是被映射但已断开网络驱动器可能让首次调用等一次 SMB 超时。
 */
function windowsVolumeRoots(): readonly string[] {
  if (volumeRootCache !== undefined) return volumeRootCache
  const roots: string[] = []
  for (let code = 'A'.charCodeAt(0); code <= 'Z'.charCodeAt(0); code++) {
    const root = `${String.fromCharCode(code)}:\\`
    if (existsSync(root)) roots.push(root)
  }
  volumeRootCache = roots
  return roots
}

/**
 * 本机沙箱 Provider：把 `argv` 包成某个真实机制的受限 argv。
 *
 * 契约与 `@tnega/sandbox` 的 Definition 一致，另外三条是本 Provider 自己的取舍：
 *
 * - **功能性探测，不是版本探测**：`bwrap --version` 能成功而 user namespace 被禁；
 *   所以探测真的跑一次受限 `true`。判决按 Provider 生命周期缓存，装卸 runner 需要
 *   重新挂载。
 * - **fail closed**：链上没有任何机制可用时抛 `SandboxUnavailableError`，绝不返回
 *   原始 argv。
 * - **副作用自己登记**：Windows 的 ACL 授权与私有临时目录在 `ctx.fiber.effect` 里
 *   登记清理；清理失败只记日志，不打断 teardown。
 */
export class LocalSandboxService extends SandboxService {
  private readonly config: Config
  private readonly platform: NodeJS.Platform
  private selection: Selection | undefined
  private probeFailure: string | undefined
  private mxc: MxcSelection | undefined
  private readonly standingGrants = new Map<string, AclGrant>()
  /** 进行中的常驻授予：并发的命令共用同一次整树传播，而不是排队各做一遍。 */
  private readonly pendingStandingGrants = new Map<string, Promise<void>>()
  private readonly tempGrants = new Map<string, TempGrant>()
  /** 每份网络策略一个过滤代理，按需启动，dispose 时关闭。 */
  private readonly proxies = new Map<string, Promise<ProxyHandle>>()
  /** 只为单次调用起的代理；`release()` 或 dispose 时关闭。 */
  private readonly ownProxies = new Set<ProxyHandle>()
  /** 机制能否强制某种网络模式：`undefined` 能，字符串是不能的原因。按生命周期缓存。 */
  private readonly networkSupport = new Map<string, string | undefined>()

  constructor(ctx: Context, config: Config = {}) {
    super(ctx)
    this.config = config
    this.platform = config.internals?.platform ?? process.platform
    const probeTimeoutMs = config.probeTimeoutMs ?? DEFAULT_PROBE_TIMEOUT_MS
    if (!Number.isFinite(probeTimeoutMs) || probeTimeoutMs <= 0) {
      // spawnSync 的 timeout: 0 在 Node 语义里是「无超时」，不能拿它当默认值。
      throw new TypeError('sandbox probeTimeoutMs must be a positive finite number')
    }
    if ((config.runnerCommand === undefined) !== (config.runnerFailureSignatures === undefined)) {
      throw new TypeError('runnerCommand and runnerFailureSignatures must be configured together')
    }
    if (config.runnerFailureSignatures?.some(signature => !signature.trim() || signature.includes('\n'))) {
      throw new TypeError('runnerFailureSignatures must be non-empty single-line strings')
    }
    ctx.fiber.effect(() => () => this.releaseGrants(), 'dispose sandbox grants')
    ctx.fiber.effect(() => () => this.closeProxies(), 'close sandbox network proxies')
  }

  override async status(): Promise<SandboxBackendStatus> {
    try {
      const selection = await this.select('read-only')
      const unsupported = await this.networkUnsupported(selection.runner, 'allowlist')
      return {
        available: true,
        runner: selection.runner,
        enforcement: selection.enforcement,
        network: unsupported === undefined ? { filtering: true } : { filtering: false, detail: unsupported },
      }
    } catch (error) {
      return {
        available: false,
        detail: error instanceof Error ? error.message : String(error),
      }
    }
  }

  protected override async runConfine(request: SandboxMechanismRequest): Promise<ConfinedArgv> {
    assertTempOutsideWorkspace(request.policy)
    const selection = await this.select(request.policy.mode)
    const network = await this.prepareNetwork(selection.runner, request.policy, request.reportDenials === true)
    const runnerArgv = selection.runner === 'windows-acl'
      ? await this.confineWindowsAcl(request.policy, request.argv)
      : selection.runner === 'windows-mxc'
        ? await this.confineWindowsMxc(request.policy, request.argv, network?.proxy)
        : this.wrapArgv(selection.runner, request.policy, request.argv, network)
    const runnerEnv = this.nodeRunnerEnv(selection.runner, runnerArgv[0])
    const env = runnerEnv || network?.env ? { ...network?.env, ...runnerEnv } : undefined
    const startedAt = Date.now()
    const proxy = network?.proxy
    // 专属代理只属于这一次调用：说明取完就收，否则每个命令留一个监听口。
    const own = network?.own === true
    const release = own && proxy !== undefined
      ? () => { void this.closeHandle(proxy) }
      : undefined

    return {
      argv: runnerArgv,
      ...(env ? { env } : {}),
      runner: selection.runner,
      enforcement: selection.enforcement,
      denialSignatures: denialSignatures(selection.runner, this.config),
      runnerFailureRules: runnerFailureRules(selection.runner, this.config),
      ...(proxy ? { annotate: () => describeDenials(proxy.proxy.denialsSince(startedAt)) } : {}),
      ...(release ? { release } : {}),
    }
  }

  /**
   * 网络受限时：确认选中的机制做得到（做不到就 fail closed），`allowlist` 再拿到这份策略的
   * 过滤代理，以及命令里客户端找到它所需的环境变量。
   *
   * `perCall` 为真时（调用方要读这一条的 `annotate()`）起一个**只服务本次调用**的代理：
   * 拒绝记录因此不会被同时跑着的后台任务或子命令污染。代价是每条命令多一次监听。
   */
  private async prepareNetwork(
    runner: SandboxRunnerName,
    policy: SandboxPolicy,
    perCall: boolean,
  ): Promise<{ proxy?: ProxyHandle; env?: Record<string, string>; own?: boolean } | undefined> {
    const network = policy.network
    if (network === undefined || !isNetworkRestricted(network)) return undefined
    const mode = network.mode === 'off' ? 'off' : 'allowlist'
    const unsupported = await this.networkUnsupported(runner, mode)
    if (unsupported !== undefined) {
      throw new SandboxError(
        `network access for commands is set to "${mode === 'off' ? 'off' : 'listed domains only'}", but ${unsupported}; `
        + 'refusing to run the command with open network. Fix that, or set command network back to open.',
        'SANDBOX_UNAVAILABLE',
      )
    }
    if (mode === 'off') return {}
    const proxy = perCall ? await this.startProxy(runner === 'bwrap', network) : await this.proxyFor(runner, network)
    if (perCall) this.ownProxies.add(proxy)
    if (runner === 'windows-mxc') return { proxy, ...(perCall ? { own: true } : {}) }
    const port = 'port' in proxy.proxy.address ? proxy.proxy.address.port : LINUX_PROXY_PORT
    return { proxy, env: proxyEnvironment(port, this.platform, this.config.socatPath ?? 'socat'), ...(perCall ? { own: true } : {}) }
  }

  /**
   * 选中的机制能否强制这种网络模式：`undefined` 能，字符串是不能的原因（面向人类）。
   *
   * - bwrap：`--unshare-net` 要真的能建 network namespace（探一次）；`allowlist` 还要 socat。
   * - seatbelt：profile 里的 `network*` 规则，总能做到。
   * - windows-mxc：`off` 是 MXC 的完全阻断；`allowlist` 要这台机器的 MXC 接受代理模式（探一次）。
   * - landlock、windows-acl、custom：管不了网络。
   */
  private async networkUnsupported(runner: SandboxRunnerName, mode: 'allowlist' | 'off'): Promise<string | undefined> {
    const key = `${runner}:${mode}`
    if (this.networkSupport.has(key)) return this.networkSupport.get(key)
    const override = this.config.internals?.networkProbe
    const verdict = override !== undefined ? override(runner, mode) : await this.probeNetwork(runner, mode)
    this.networkSupport.set(key, verdict)
    return verdict
  }

  private async probeNetwork(runner: SandboxRunnerName, mode: 'allowlist' | 'off'): Promise<string | undefined> {
    const timeout = this.config.probeTimeoutMs ?? DEFAULT_PROBE_TIMEOUT_MS
    switch (runner) {
      case 'bwrap': {
        const probe = { ...this.probePolicy(), network: { mode: 'off', allowedDomains: [], deniedDomains: [] } } satisfies SandboxPolicy
        if (this.probeCommand(this.config.bwrapPath ?? 'bwrap', [...bwrapProfileArgs(probe), '--', 'true'], timeout, 'full') === 'unusable') {
          return `bubblewrap cannot create a private network here (${this.probeFailure ?? 'probe failed'})`
        }
        if (mode === 'off') return undefined
        const socat = spawnSync(this.config.socatPath ?? 'socat', ['-V'], { timeout, stdio: 'ignore' })
        return socat.status === 0 ? undefined : 'socat is not installed (install it, for example `apt install socat`)'
      }
      case 'seatbelt':
        return undefined
      case 'windows-mxc':
        return mode === 'off' ? undefined : this.probeMxcProxy(timeout)
      case 'landlock':
        return 'Landlock cannot restrict network access; install bubblewrap'
      case 'windows-acl':
        return 'the Windows ACL sandbox cannot restrict network access; install MXC (npm install -g @microsoft/mxc-sdk) on Windows 11 24H2 or later'
      case 'custom':
        return 'the custom sandbox runner does not restrict network access'
    }
  }

  /**
   * MXC 的代理模式要较新的 Windows 构建（26100.9278 / 26200.9278 起）。真跑一次只读的
   * `exit 0`，请求里带代理模式的网络块；MXC 不在探测里连代理，所以端点只要是回环 URL 即可。
   */
  private async probeMxcProxy(timeout: number): Promise<string | undefined> {
    try {
      const mxc = await windowsMxc()
      const selected = this.mxc ?? await this.locateMxc(mxc)
      const [command, ...prefix] = mxc.resolveMxcRunnerCommand({
        ...(this.config.windowsMxcRunnerCommand ? { runnerCommand: this.config.windowsMxcRunnerCommand } : {}),
      })
      if (command === undefined) return 'the MXC runner command is empty'
      const workspace = this.probePolicy().workspaceRoot
      const env = this.nodeRunnerEnv('windows-mxc', command)
      const result = await localExecutionProvider.runProcess({
        argv: [command, ...prefix, ...mxc.buildRunnerArgs({
          workspace, mode: 'read-only', exec: selected.exec, contract: selected.contract,
          containerId: mxc.containerIdFor(workspace, 'probe-network'),
          network: { proxy: 'http://127.0.0.1:9' },
          readOnlyRoots: [], execArgs: [],
          argv: ['cmd.exe', '/d', '/s', '/c', 'exit 0'],
        })],
        cwd: workspace,
        timeoutMs: Math.max(timeout, DEFAULT_MXC_PROBE_TIMEOUT_MS),
        maxBuffer: 64 * 1024,
        ...(env ? { env } : {}),
      })
      if (result.exitCode === 0) return undefined
      return `this Windows build's MXC does not accept proxy mode (${result.stderr.trim() || `runner exited ${result.exitCode}`}); `
        + 'update Windows 11 to build 26100.9278 / 26200.9278 or later'
    } catch (error) {
      return `MXC proxy mode could not be checked: ${error instanceof Error ? error.message : String(error)}`
    }
  }

  /**
   * 这份网络策略的过滤代理。Linux 上它监听私有目录里的 Unix socket（绑定进 bwrap 后由 socat
   * 接回 TCP）；macOS 与 Windows 监听 `127.0.0.1` 的随机端口（Seatbelt 与 MXC 只放行它）。
   */
  private proxyFor(runner: SandboxRunnerName, network: SandboxNetworkPolicy): Promise<ProxyHandle> {
    const unix = runner === 'bwrap'
    const key = JSON.stringify([unix, network.allowedDomains, network.deniedDomains])
    let pending = this.proxies.get(key)
    if (pending === undefined) {
      pending = this.startProxy(unix, network)
      // 起不来的代理不缓存：下一条命令重试。
      pending.catch(() => this.proxies.delete(key))
      this.proxies.set(key, pending)
    }
    return pending
  }

  private async startProxy(unix: boolean, network: SandboxNetworkPolicy): Promise<ProxyHandle> {
    const options = {
      decide: (target: { host: string; port: number }) => decideNetworkAccess(network, target.host, target.port),
      upstream: () => this.config.upstreamProxy?.() ?? environmentUpstream(),
      noProxy: this.config.noProxy ?? (() => process.env.NO_PROXY ?? process.env.no_proxy),
      onDecision: (decision: ProxyDecision) => {
        void this.reportNetwork({
          host: decision.host,
          port: decision.port,
          protocol: decision.protocol,
          allowed: decision.allowed,
          reason: decision.reason,
          ...(decision.rule !== undefined ? { rule: decision.rule } : {}),
          at: decision.at,
        })
      },
    }
    if (!unix) return { proxy: await startNetworkProxy({ host: '127.0.0.1', port: 0 }, options) }
    const socketDir = canonicalPath(await mkdtemp(join(tmpdir(), 'tnega-net-')))
    try {
      // 目录只给本账户：别的本地用户不该借这个 socket 出网。
      await chmod(socketDir, 0o700)
      return { proxy: await startNetworkProxy({ path: join(socketDir, 'proxy.sock') }, options), socketDir }
    } catch (error) {
      await rm(socketDir, { recursive: true, force: true }).catch(() => undefined)
      throw error
    }
  }

  private async closeProxies(): Promise<void> {
    const handles = await Promise.allSettled([...this.proxies.values(), ...this.ownProxies])
    this.proxies.clear()
    this.ownProxies.clear()
    for (const handle of handles) {
      if (handle.status !== 'fulfilled') continue
      await this.closeHandle(handle.value)
    }
  }

  /** 关掉一个代理，并删掉它自己的 socket 目录（只有 bwrap 的代理有）。 */
  private async closeHandle(handle: ProxyHandle | Promise<ProxyHandle>): Promise<void> {
    let settled: ProxyHandle
    try {
      settled = await handle
    } catch {
      return
    }
    this.ownProxies.delete(settled)
    await settled.proxy.close().catch(() => undefined)
    if (settled.socketDir !== undefined) {
      await rm(settled.socketDir, { recursive: true, force: true }).catch(() => undefined)
    }
  }

  /** POSIX 三个机制与 custom 都只是 argv 变换：机制自己声明 profile，不做别的。 */
  private wrapArgv(
    runner: SandboxRunnerName,
    policy: SandboxPolicy,
    argv: readonly string[],
    network?: { proxy?: ProxyHandle },
  ): string[] {
    const proxy = network?.proxy
    switch (runner) {
      case 'bwrap': {
        const socketDir = proxy?.socketDir
        const relay = socketDir === undefined
          ? []
          : linuxRelayArgv(this.config.socatPath ?? 'socat', join(socketDir, 'proxy.sock'))
        return [this.config.bwrapPath ?? 'bwrap', ...bwrapProfileArgs(policy, socketDir), '--', ...relay, ...argv]
      }
      case 'landlock':
        return [
          this.config.landlockLauncher ?? 'landlock-run',
          ...landlockProfileArgs(policy),
          '--',
          ...argv,
        ]
      case 'seatbelt':
        return [
          this.config.sandboxExecPath ?? 'sandbox-exec',
          ...seatbeltProfileArgs(policy, proxy && 'port' in proxy.proxy.address ? proxy.proxy.address.port : undefined),
          '--',
          ...argv,
        ]
      case 'custom':
        return [
          ...(this.config.runnerCommand ?? []),
          '--mode', policy.mode,
          '--workspace', policy.workspaceRoot,
          '--temp', this.tempBase(policy),
          '--',
          ...argv,
        ]
      case 'windows-acl':
      case 'windows-mxc':
        throw new SandboxUnavailableError(
          policy.mode,
          `${runner} requires asynchronous preparation`,
        )
    }
  }

  /**
   * 两个 Windows runner 都是 Node 脚本。桌面端用 Electron 自己的可执行文件以 Node 模式跑
   * 它们；内置 runner 再带上标记，让桌面端改用不触发忙光标的 utility process 承载
   * （见 `apps/desktop/src/process-launcher.ts`）。显式配置的 runner 不带标记。
   */
  private nodeRunnerEnv(runner: SandboxRunnerName, command: string | undefined): Record<string, string> | undefined {
    if (runner !== 'windows-acl' && runner !== 'windows-mxc') return undefined
    if (!process.versions.electron || command !== process.execPath) return undefined
    const configured = runner === 'windows-acl' ? this.config.windowsAclRunnerCommand : this.config.windowsMxcRunnerCommand
    return { ELECTRON_RUN_AS_NODE: '1', ...(configured === undefined ? { TNEGA_DESKTOP_ACL_RUNNER: '1' } : {}) }
  }

  /**
   * Windows MXC：Provider 不落任何 ACE。runner 为每次调用写一份 MXC 请求（模式、工作区、
   * `workspace-write` 的私有 temp、读根），MXC 在请求结束时撤销它自己的策略，所以这里只拼 argv。
   */
  private async confineWindowsMxc(policy: SandboxPolicy, argv: readonly string[], proxy?: ProxyHandle): Promise<string[]> {
    const mxc = await windowsMxc()
    const selected = this.mxc ?? await this.locateMxc(mxc)
    const workspaceRoot = canonicalPath(policy.workspaceRoot)
    const tempBase = this.tempBase(policy)
    if (policy.mode === 'workspace-write' && containsDirectory(workspaceRoot, tempBase)) {
      throw new SandboxError(`sandbox tempRoot must live outside the workspace: ${tempBase}`, 'SANDBOX_INVALID_POLICY')
    }
    const runnerCommand = mxc.resolveMxcRunnerCommand({
      ...(this.config.windowsMxcRunnerCommand ? { runnerCommand: this.config.windowsMxcRunnerCommand } : {}),
    })
    return [...runnerCommand, ...mxc.buildRunnerArgs({
      workspace: workspaceRoot,
      mode: policy.mode,
      exec: selected.exec,
      contract: selected.contract,
      containerId: mxc.containerIdFor(workspaceRoot, policy.sessionId),
      ...(policy.mode === 'workspace-write' ? { temp: tempBase } : {}),
      ...(policy.network?.mode === 'off'
        ? { network: 'off' as const }
        : proxy && 'port' in proxy.proxy.address
          ? { network: { proxy: `http://127.0.0.1:${proxy.proxy.address.port}` } }
          : {}),
      readOnlyRoots: this.mxcReadRoots(),
      execArgs: [],
      argv,
    })]
  }

  /** 交给 MXC 的只读根：默认全卷 + 显式配置；`mxcRead: 'roots'` 时只有显式配置。 */
  private mxcReadRoots(): string[] {
    const configured = (this.config.mxcReadOnlyRoots ?? []).map(canonicalPath)
    if ((this.config.mxcRead ?? 'all') === 'roots') return configured
    return [...this.volumeRoots().map(canonicalPath), ...configured]
  }

  private volumeRoots(): readonly string[] {
    if (this.platform !== 'win32') return []
    return (this.config.internals?.volumeRoots ?? windowsVolumeRoots)()
  }

  /** 选执行器；探测被注入（测试）时契约取配置值或首选版本。 */
  private async locateMxc(mxc: WindowsMxcModule): Promise<MxcSelection> {
    const exec = mxc.locateMxcExecutor({
      ...(this.config.mxcExecPath !== undefined ? { configuredPath: this.config.mxcExecPath } : {}),
    })
    this.mxc = { exec, contract: this.config.mxcContractVersion ?? mxc.MXC_CONTRACT_VERSIONS[0] }
    return this.mxc
  }

  /**
   * Windows：受限令牌由 runner 自己从它的令牌派生，所以 Provider 这边要做的是把
   * **可写白名单**准备好 —— 给派生出来的 capability SID 授权。
   *
   * 生命周期取舍（与 `@tnega/sandbox-windows-acl` 的 README 一致）：
   *
   * - **workspace 的 standing ACE 常驻**，作为跨 Session 的复用缓存：撤销它会迫使下
   *   一次重新做整树传播，而它是一个只有本 Provider 会命名的随机 SID。
   * - **私有 temp 的 ACE 可撤销**，`dispose` 时撤销并删目录；每个 (session, workspace)
   *   一个，互不共享。
   */
  private async confineWindowsAcl(
    policy: SandboxPolicy,
    argv: readonly string[],
  ): Promise<string[]> {
    const acl = await windowsAcl()
    const workspaceRoot = canonicalPath(policy.workspaceRoot)
    const tempBase = this.tempBase(policy)
    acl.assertTempRootOutsideWorkspace(workspaceRoot, tempBase)

    const runnerCommand = this.config.windowsAclRunnerCommand ?? await acl.resolveRunnerCommand()
    const workspaceSid = acl.workspaceWriteSid(workspaceRoot)
    let tempDir = tempBase
    let tempSid: string | undefined

    if (policy.mode === 'workspace-write') {
      await this.ensureStandingGrant(acl, workspaceRoot, workspaceSid)
      const key = `${policy.sessionId ?? ''}\u0000${workspaceRoot}`
      const existing = this.tempGrants.get(key)
      if (existing) {
        tempDir = existing.dir
        tempSid = existing.sid
      } else {
        const created = await mkdtemp(join(tempBase, 'tnega-sbx-'))
        const dir = canonicalPath(created)
        const sid = acl.tempWriteSid(dir)
        // 只跟**实际会被授予**的目录比：Windows 上环境临时根（`os.tmpdir()`）永不隐式
        // 授权，而私有 temp 恰恰建在它下面 —— 拿 `writableRoots`（含 tmpdir）来比会把
        // 每一次 workspace-write 都判成冲突。
        acl.assertPrivateTempDisjoint(dir, [workspaceRoot])
        const grant = await acl.AclWriteGrant.create(sid, this.aclGrantOptions(acl))
        try {
          await grant.add(dir, { revocable: true })
        } catch (error) {
          await grant.dispose().catch(() => undefined)
          await rm(dir, { recursive: true, force: true }).catch(() => undefined)
          throw error
        }
        this.tempGrants.set(key, { dir, sid, grant })
        tempDir = dir
        tempSid = sid
      }
    }

    return [
      ...runnerCommand,
      '--workspace', workspaceRoot,
      '--temp', tempDir,
      '--mode', policy.mode,
      ...(tempSid === undefined ? [] : ['--write-sid', workspaceSid, '--temp-write-sid', tempSid]),
      '--',
      ...argv,
    ]
  }

  private async ensureStandingGrant(
    acl: WindowsAclModule,
    workspaceRoot: string,
    workspaceSid: string,
  ): Promise<void> {
    if (this.standingGrants.has(workspaceRoot)) return
    let pending = this.pendingStandingGrants.get(workspaceRoot)
    if (pending === undefined) {
      pending = this.createStandingGrant(acl, workspaceRoot, workspaceSid)
        .then(grant => { this.standingGrants.set(workspaceRoot, grant) })
        // 失败的授予不留下记录：下一次命令重新尝试。
        .finally(() => this.pendingStandingGrants.delete(workspaceRoot))
      this.pendingStandingGrants.set(workspaceRoot, pending)
    }
    await pending
  }

  private async createStandingGrant(
    acl: WindowsAclModule,
    workspaceRoot: string,
    workspaceSid: string,
  ): Promise<AclGrant> {
    const grant = await acl.AclWriteGrant.create(workspaceSid, this.aclGrantOptions(acl))
    try {
      await grant.add(workspaceRoot, { revocable: false })
    } catch (error) {
      await grant.dispose().catch(() => undefined)
      throw explainGrantDenied(acl, workspaceRoot, error)
    }
    return grant
  }

  /**
   * 授予/撤销交给 helper 进程：首次授予要把可继承 ACE 同步传播到整棵 workspace 树，大仓库
   * 上能跑几十分钟。桌面端的 Runtime 跑在 Electron 主进程里，在本进程内做这件事会冻结 UI 与
   * 本地 server。helper 与 runner 一样以 Node 模式启动（桌面端经 utility process 承载）；不设
   * 超时——中途杀掉会留下一棵只传播了一半的树。仓库内 dev 形态没有 helper 产物，退回进程内执行。
   */
  private aclGrantOptions(acl: WindowsAclModule): AclGrantOptions {
    const command = acl.resolveGrantCommand()
    if (command === undefined) return {}
    const electronNode = Boolean(process.versions.electron) && command[0] === process.execPath
    return {
      operate: async (operation) => {
        const result = await localExecutionProvider.runProcess({
          argv: [...command, ...acl.grantCommandArgs(operation)],
          cwd: tmpdir(),
          timeoutMs: 0,
          maxBuffer: 64 * 1024,
          ...(electronNode ? { env: { ELECTRON_RUN_AS_NODE: '1', TNEGA_DESKTOP_ACL_RUNNER: '1' } } : {}),
        })
        return acl.parseGrantReply(result, operation)
      },
    }
  }

  private tempBase(policy: SandboxPolicy): string {
    return canonicalPath(policy.tempRoot ?? this.config.tempRoot ?? tmpdir())
  }

  /**
   * 选中机制：链上第一个功能性探测通过的。
   *
   * 探测判决（含「不可用」）按 Provider 生命周期缓存 —— 探测要起进程，而机制不会在
   * 一次运行中间出现或消失。
   */
  private async select(mode: ConfinedSandboxMode): Promise<Selection> {
    if (this.selection !== undefined) return this.selection
    const chain = this.config.internals?.chain ?? this.config.chain
      ?? PLATFORM_CHAINS[this.platform] ?? []
    const failures: string[] = []
    for (const runner of chain) {
      const probed = await this.probeRunner(runner)
      if (probed !== 'unusable') {
        this.selection = { runner, enforcement: probed }
        return this.selection
      }
      failures.push(`${runner}: ${this.probeFailure ?? 'probe failed'}`)
    }
    const detail = failures.length > 0
      ? failures.join('; ')
      : `no sandbox mechanism is configured for platform "${this.platform}"`
    throw new SandboxUnavailableError(mode, detail)
  }

  private async probeRunner(runner: SandboxRunnerName): Promise<SandboxEnforcement | 'unusable'> {
    this.probeFailure = undefined
    const override = this.config.internals?.probe
    if (override !== undefined) {
      const verdict = override(runner)
      if (verdict === 'unusable') this.probeFailure = 'injected probe'
      return verdict
    }
    const timeout = this.config.probeTimeoutMs ?? DEFAULT_PROBE_TIMEOUT_MS
    switch (runner) {
      case 'bwrap':
        return this.probeCommand(
          this.config.bwrapPath ?? 'bwrap',
          [...bwrapProfileArgs(this.probePolicy()), '--', 'true'],
          timeout,
          STATIC_ENFORCEMENT.bwrap,
        )
      case 'seatbelt':
        return this.probeCommand(
          this.config.sandboxExecPath ?? 'sandbox-exec',
          [...seatbeltProfileArgs(this.probePolicy()), '--', 'true'],
          timeout,
          STATIC_ENFORCEMENT.seatbelt,
        )
      case 'landlock': {
        const result = spawnSync(this.config.landlockLauncher ?? 'landlock-run', ['--probe'], {
          timeout,
          encoding: 'utf8',
          windowsHide: true,
        })
        if (result.status !== 0) {
          this.probeFailure = probeDetail(result)
          return 'unusable'
        }
        // launcher 自己报告协商到的 ABI：宁可如实说 partial，也不夸大。
        return (result.stdout ?? '').includes('partially enforced') ? 'partial' : 'full'
      }
      case 'windows-acl':
        return this.probeWindowsAcl(timeout)
      case 'windows-mxc':
        return this.probeWindowsMxc(this.config.probeTimeoutMs ?? DEFAULT_MXC_PROBE_TIMEOUT_MS)
      case 'custom':
        if (this.config.runnerCommand === undefined || this.config.runnerCommand.length === 0) {
          this.probeFailure = 'runnerCommand is not configured'
          return 'unusable'
        }
        return STATIC_ENFORCEMENT.custom
    }
  }

  private async probeWindowsAcl(timeout: number): Promise<SandboxEnforcement | 'unusable'> {
    const acl = await windowsAcl().catch((error: unknown) => {
      this.probeFailure = error instanceof Error ? error.message : String(error)
      return undefined
    })
    if (acl === undefined) return 'unusable'
    if (!(await acl.isWindowsAclAvailable())) {
      this.probeFailure = 'koffi (FFI) is not available'
      return 'unusable'
    }
    const runnerCommand = this.config.windowsAclRunnerCommand ?? await acl.resolveRunnerCommand()
    const [command, ...prefix] = runnerCommand
    if (command === undefined) {
      this.probeFailure = 'windows-acl runner command is empty'
      return 'unusable'
    }
    try {
      const result = await localExecutionProvider.runProcess({
        argv: [
          command, ...prefix,
          '--workspace', this.probePolicy().workspaceRoot,
          '--temp', tmpdir(),
          '--mode', 'read-only',
          '--', 'cmd.exe', '/d', '/s', '/c', 'exit 0',
        ],
        cwd: this.probePolicy().workspaceRoot,
        timeoutMs: timeout,
        maxBuffer: 64 * 1024,
        ...(process.versions.electron && command === process.execPath
          ? { env: { ELECTRON_RUN_AS_NODE: '1',
            ...(this.config.windowsAclRunnerCommand === undefined ? { TNEGA_DESKTOP_ACL_RUNNER: '1' } : {}),
          } } : {}),
      })
      if (result.exitCode === 0) return STATIC_ENFORCEMENT['windows-acl']
      this.probeFailure = result.stderr.trim() || `runner exited ${result.exitCode}`
    } catch (error) {
      this.probeFailure = error instanceof Error ? error.message : String(error)
    }
    return 'unusable'
  }

  /**
   * MXC 的功能性探测：真的经 runner 在 MXC 里跑一次只读的 `exit 0`。它同时回答三个问题：
   * 执行器在不在、宿主够不够 MXC 的版本下限、执行器接受哪个请求契约 —— 按
   * `MXC_CONTRACT_VERSIONS` 依次试，第一个跑通的被记住。
   *
   * 探测不申请读根：它跑的是 `cmd exit 0`，不需要工具链；只读根按每次调用实际计算。
   */
  private async probeWindowsMxc(timeout: number): Promise<SandboxEnforcement | 'unusable'> {
    if (this.platform !== 'win32') {
      this.probeFailure = 'MXC confines on Windows only'
      return 'unusable'
    }
    if (this.config.mxcExecPath === undefined && process.env.TNEGA_MXC_EXEC?.toLowerCase() === 'off') {
      this.probeFailure = 'disabled by TNEGA_MXC_EXEC=off'
      return 'unusable'
    }
    try {
      const mxc = await windowsMxc()
      const exec = mxc.locateMxcExecutor({
        ...(this.config.mxcExecPath !== undefined ? { configuredPath: this.config.mxcExecPath } : {}),
      })
      const runnerCommand = mxc.resolveMxcRunnerCommand({
        ...(this.config.windowsMxcRunnerCommand ? { runnerCommand: this.config.windowsMxcRunnerCommand } : {}),
      })
      const [command, ...prefix] = runnerCommand
      if (command === undefined) throw new Error('windows-mxc runner command is empty')
      const workspace = this.probePolicy().workspaceRoot
      const contracts = this.config.mxcContractVersion !== undefined
        ? [this.config.mxcContractVersion]
        : mxc.MXC_CONTRACT_VERSIONS
      const env = this.nodeRunnerEnv('windows-mxc', command)
      const failures: string[] = []
      for (const contract of contracts) {
        const result = await localExecutionProvider.runProcess({
          argv: [command, ...prefix, ...mxc.buildRunnerArgs({
            workspace, mode: 'read-only', exec, contract,
            containerId: mxc.containerIdFor(workspace, 'probe'),
            readOnlyRoots: [], execArgs: [],
            argv: ['cmd.exe', '/d', '/s', '/c', 'exit 0'],
          })],
          cwd: workspace,
          timeoutMs: timeout,
          maxBuffer: 64 * 1024,
          ...(env ? { env } : {}),
        })
        if (result.exitCode === 0) {
          this.mxc = { exec, contract }
          return STATIC_ENFORCEMENT['windows-mxc']
        }
        failures.push(`contract ${contract}: ${result.stderr.trim() || `runner exited ${result.exitCode}`}`)
      }
      this.probeFailure = failures.join('; ')
    } catch (error) {
      this.probeFailure = error instanceof Error ? error.message : String(error)
    }
    return 'unusable'
  }

  private probeCommand(
    command: string,
    args: readonly string[],
    timeout: number,
    enforcement: SandboxEnforcement,
    env?: Readonly<Record<string, string>>,
  ): SandboxEnforcement | 'unusable' {
    const result = spawnSync(command, [...args], {
      timeout, stdio: 'ignore', windowsHide: true,
      ...(env ? { env: { ...process.env, ...env } } : {}),
    })
    if (result.status === 0) return enforcement
    this.probeFailure = probeDetail(result)
    return 'unusable'
  }

  /** 探测用的中性策略：只读不需要绑定任何可写根，所以用 cwd 即可。 */
  private probePolicy(): SandboxPolicy {
    return {
      mode: 'read-only',
      workspaceRoot: canonicalPath(this.config.workspaceRoot ?? process.cwd()),
    }
  }

  /**
   * teardown：撤销可撤销的 temp 授权并删除私有临时目录，释放 SID。
   *
   * 失败只记日志、不抛 —— cordis 的 teardown 不该被授权清理打断；常驻 workspace ACE
   * 是预期终态，不是残留。
   */
  private async releaseGrants(): Promise<void> {
    const failures: unknown[] = []
    for (const entry of this.tempGrants.values()) {
      await entry.grant.revoke(entry.dir).catch((error: unknown) => failures.push(error))
      await entry.grant.dispose().catch((error: unknown) => failures.push(error))
      await rm(entry.dir, { recursive: true, force: true })
        .catch((error: unknown) => failures.push(error))
    }
    this.tempGrants.clear()
    // 仍在传播中的常驻授予不等：常驻 ACE 本来就不撤销，dispose 只释放 SID。
    for (const grant of this.standingGrants.values()) {
      await grant.dispose().catch((error: unknown) => failures.push(error))
    }
    this.standingGrants.clear()
    if (failures.length > 0) {
      this.ctx.logger('sandbox-local').warn('sandbox grant cleanup failed', failures)
    }
  }
}

/**
 * 私有临时区不能落在工作区之内：那样它会被当成「工作区内的路径」，从而把「临时写」
 * 与「工作区写」混成一件事 —— Windows 上还会让 temp ACE 直接放大工作区的可写面。
 */
function assertTempOutsideWorkspace(policy: SandboxPolicy): void {
  if (policy.tempRoot === undefined) return
  if (containsDirectory(policy.workspaceRoot, policy.tempRoot)) {
    throw new SandboxError(
      `sandbox tempRoot must live outside the workspace: ${policy.tempRoot}`,
      'SANDBOX_INVALID_POLICY',
    )
  }
}

function probeDetail(result: { status: number | null; error?: Error | undefined; signal?: NodeJS.Signals | null }): string {  if (result.error !== undefined) return result.error.message
  if (result.signal != null) return `killed by ${result.signal}`
  return `exit code ${String(result.status)}`
}

function denialSignatures(runner: SandboxRunnerName, config: Config): readonly string[] {
  if (runner === 'custom' && config.runnerDenialSignatures !== undefined) {
    return config.runnerDenialSignatures
  }
  return DENIAL_SIGNATURES[runner]
}

function runnerFailureRules(runner: SandboxRunnerName, config: Config): readonly RunnerFailureRule[] {
  if (runner === 'custom') {
    const signatures = config.runnerFailureSignatures
    if (signatures === undefined || signatures.length === 0) return []
    return [{ fatalSignatures: signatures }]
  }
  return RUNNER_FAILURE_RULES[runner]
}

/** Win32 `ERROR_ACCESS_DENIED`. */
const ERROR_ACCESS_DENIED = 5

/**
 * The workspace grant edits the root folder's DACL, which needs the caller to
 * own the folder (or hold WRITE_DAC on it). A folder owned by another account,
 * for example one created from an administrator prompt, refuses that; say so
 * and what to do, instead of a bare Win32 code. The command still does not run.
 */
function explainGrantDenied(acl: WindowsAclModule, workspaceRoot: string, error: unknown): unknown {
  if (!(error instanceof acl.Win32Error) || error.win32Code !== ERROR_ACCESS_DENIED) return error
  return new Error(
    `cannot sandbox commands in ${workspaceRoot}: your account is not allowed to change this folder's permissions, `
    + 'which the Windows sandbox needs to let commands write here. The folder is usually owned by another account '
    + `(for example, it was created from an administrator prompt). Make your account its owner (takeown /f "${workspaceRoot}" /r /d y), `
    + `or run this session with Full access. (${error.message})`,
    { cause: error },
  )
}

export const name = '@tnega/sandbox-local'

/** 挂载入口：`await ctx.plugin(sandboxLocal, { workspaceRoot: cwd })`。 */
export const sandboxLocal = {
  name: 'sandbox-local',
  apply(ctx: Context, config: Config = {}) {
    new LocalSandboxService(ctx, config)
  },
}
